import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/9b8e18d6-09b4-41a8-b198-0ce4e2da7c92.png" alt="FCCE B.V. Logo" className="h-12 w-12 object-contain" />
              <h1 className="text-2xl font-bold text-gray-900">FCCE B.V.</h1>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer font-medium">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer font-medium">About</button>
              <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer font-medium">Products</button>
              <button onClick={() => scrollToSection('features')} className="text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer font-medium">Features</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer font-medium">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50"></div>
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-purple-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <h2 className="text-6xl font-bold text-gray-900 leading-tight">Modern iOS Applications for Enhanced Security</h2>
                <p className="text-xl text-gray-600 leading-relaxed max-w-xl">We are a team of experts developing cutting-edge applications for the iOS platform. Our products, including VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices.</p>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 rounded-3xl blur-2xl opacity-20"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl p-8">
                <div className="absolute top-6 right-6 bg-gray-900/80 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">iOS Platform</div>
                <img src="https://public.readdy.ai/ai/img_res/0ba64cfb9243270c170ce58e3bb49397.jpg" alt="VPN App Interface" className="w-full h-auto object-contain" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <i className="ri-team-line text-2xl text-teal-500"></i>
              <span className="text-teal-500 font-semibold text-lg">About Us</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Team of Experts Developing<br />Modern Applications</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">FCCE B.V. is dedicated to creating innovative iOS applications that prioritize user security and experience. Our team combines technical expertise with creative design to deliver products that make a real difference in mobile device protection.</p>
              <p className="text-lg text-gray-700 leading-relaxed">With years of experience in iOS development, we understand the evolving needs of mobile users. Our commitment to excellence drives us to continuously improve and innovate, ensuring our applications remain at the forefront of mobile security technology.</p>
              <p className="text-lg text-gray-700 leading-relaxed">We believe in building applications that are not only powerful but also intuitive and user-friendly. Every feature we develop is carefully crafted to provide maximum value while maintaining the highest standards of security and performance.</p>
            </div>

            <div className="space-y-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-shield-check-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Security First</h3>
                <p className="text-gray-700 leading-relaxed">Our applications are built with security as the top priority, protecting user data and privacy at every level.</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-purple-500 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-smartphone-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">iOS Expertise</h3>
                <p className="text-gray-700 leading-relaxed">Specialized knowledge in iOS development ensures optimal performance and seamless integration with Apple devices.</p>
              </div>

              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-8 hover:shadow-lg transition-shadow">
                <div className="w-16 h-16 bg-teal-500 rounded-2xl flex items-center justify-center mb-4">
                  <i className="ri-user-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">User Focused</h3>
                <p className="text-gray-700 leading-relaxed">Every feature is designed with the end user in mind, creating intuitive experiences that enhance daily mobile usage.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-gradient-to-br from-gray-900 via-slate-800 to-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <i className="ri-apps-line text-2xl text-teal-400"></i>
              <span className="text-teal-400 font-semibold text-lg">Our Products</span>
            </div>
            <h2 className="text-5xl font-bold text-white mb-6">VPN & Cleaner Applications</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">Powerful tools designed to protect your privacy and optimize your device performance</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 p-8 hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="relative z-10">
                <div className="mb-8">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-shield-keyhole-line text-4xl text-white"></i>
                  </div>
                  <h3 className="text-4xl font-bold text-white mb-4">VPN Application</h3>
                  <p className="text-lg text-blue-50 leading-relaxed mb-6">Secure your internet connection with military-grade encryption. Browse anonymously, access content worldwide, and protect your data from prying eyes with our advanced VPN technology.</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <i className="ri-check-line text-2xl text-white"></i>
                      <span className="text-white font-medium">Military-grade encryption</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-check-line text-2xl text-white"></i>
                      <span className="text-white font-medium">Global server network</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-check-line text-2xl text-white"></i>
                      <span className="text-white font-medium">Zero-log policy</span>
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <img src="https://readdy.ai/api/search-image?query=elegant%20iPhone%20mockup%20showing%20VPN%20application%20interface%20with%20glowing%20blue%20shield%20icon%20connection%20toggle%20and%20server%20selection%20screen%20on%20gradient%20blue%20background%20modern%20UI%20design%20clean%20minimalist%20aesthetic%20professional%20app%20screenshot&width=400&height=500&seq=vpnprod001&orientation=portrait" alt="VPN Application" className="w-full h-auto object-contain rounded-2xl shadow-2xl transform rotate-3 group-hover:rotate-0 transition-transform duration-300" />
                </div>
              </div>
            </div>

            <div className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-600 via-purple-500 to-pink-500 p-8 hover:shadow-2xl transition-all duration-300 hover:scale-105">
              <div className="relative z-10">
                <div className="mb-8">
                  <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6">
                    <i className="ri-delete-bin-line text-4xl text-white"></i>
                  </div>
                  <h3 className="text-4xl font-bold text-white mb-4">Cleaner Application</h3>
                  <p className="text-lg text-purple-50 leading-relaxed mb-6">Optimize your device performance by removing junk files, clearing cache, and managing storage efficiently. Keep your iOS device running smoothly with intelligent cleaning algorithms.</p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <i className="ri-check-line text-2xl text-white"></i>
                      <span className="text-white font-medium">Smart storage analysis</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-check-line text-2xl text-white"></i>
                      <span className="text-white font-medium">One-tap optimization</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-check-line text-2xl text-white"></i>
                      <span className="text-white font-medium">Battery life improvement</span>
                    </div>
                  </div>
                </div>
                <div className="mt-8">
                  <img src="https://readdy.ai/api/search-image?query=modern%20iPhone%20mockup%20displaying%20cleaner%20application%20interface%20with%20circular%20storage%20meter%20purple%20gradient%20design%20clean%20button%20and%20file%20management%20screen%20on%20gradient%20purple%20background%20sleek%20UI%20minimalist%20professional%20app%20screenshot&width=400&height=500&seq=cleanprod001&orientation=portrait" alt="Cleaner Application" className="w-full h-auto object-contain rounded-2xl shadow-2xl transform -rotate-3 group-hover:rotate-0 transition-transform duration-300" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <i className="ri-star-line text-2xl text-teal-500"></i>
              <span className="text-teal-500 font-semibold text-lg">Features</span>
            </div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">Enhance User Experience &<br />Mobile Security</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-blue-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Advanced Security</h3>
              <p className="text-gray-600 leading-relaxed">Multi-layered security protocols protect your data and privacy across all our applications with industry-leading encryption standards.</p>
            </div>

            <div className="bg-white rounded-2xl p-8 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <i className="ri-flashlight-line text-3xl text-purple-600"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Fast Performance</h3>
              <p className="text-gray-600 leading-relaxed">Optimized code and efficient algorithms ensure lightning-fast performance without draining your device battery or resources.</p>
            </div>

            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl p-8 hover:shadow-xl transition-shadow flex flex-col items-center justify-center text-center">
              <div className="text-7xl font-bold text-white mb-4">99%</div>
              <div className="text-2xl font-semibold text-white">User Satisfaction</div>
              <p className="text-teal-50 mt-4">Trusted by thousands of users worldwide</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 bg-white rounded-2xl p-8 hover:shadow-xl transition-shadow flex items-center gap-8">
              <div className="w-20 h-20 bg-orange-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                <i className="ri-lock-password-line text-4xl text-orange-600"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Privacy Protection</h3>
                <p className="text-gray-600 leading-relaxed">Your privacy is our priority. We never collect, store, or share your personal data. All processing happens locally on your device.</p>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-2xl p-6 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-refresh-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Regular Updates</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Continuous improvements and new features</p>
              </div>

              <div className="bg-white rounded-2xl p-6 hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center mb-4">
                  <i className="ri-customer-service-line text-2xl text-pink-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">24/7 Support</h3>
                <p className="text-gray-600 text-sm leading-relaxed">Always here to help when you need us</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 mb-12">
            <div className="lg:col-span-1">
              <div className="flex items-center gap-3 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9b8e18d6-09b4-41a8-b198-0ce4e2da7c92.png" alt="FCCE B.V. Logo" className="h-10 w-10 object-contain" />
                <div className="text-2xl font-bold text-white">FCCE B.V.</div>
              </div>
              <h3 className="text-4xl font-bold text-white mb-4">Get in Touch</h3>
              <p className="text-gray-300 leading-relaxed">We're here to answer your questions and discuss how our applications can benefit you.</p>
            </div>

            <div>
              <h4 className="text-white font-semibold text-lg mb-6">Location</h4>
              <div className="space-y-3 text-gray-300">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-xl text-teal-400 mt-1 flex-shrink-0"></i>
                  <div>
                    <p className="leading-relaxed">tt. Vasumweg 58 D 3e verdieping</p>
                    <p>Amsterdam</p>
                    <p>1033 SC</p>
                    <p>Netherlands</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-lg mb-6">Contact</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <i className="ri-mail-line text-xl text-teal-400 flex-shrink-0"></i>
                  <a href="mailto:info@fccebv.com" className="text-gray-300 hover:text-white transition-colors cursor-pointer">info@fccebv.com</a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-xl text-teal-400 flex-shrink-0"></i>
                  <a href="tel:+16187775704" className="text-gray-300 hover:text-white transition-colors cursor-pointer">+1 6187775704</a>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-white font-semibold text-lg mb-6">Navigation</h4>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">Home</button>
                <button onClick={() => scrollToSection('about')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">About</button>
                <button onClick={() => scrollToSection('products')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">Products</button>
                <button onClick={() => scrollToSection('features')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">Features</button>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-600 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="text-gray-400 text-sm">© 2025 FCCE B.V. All rights reserved</div>
              <div className="flex items-center gap-6">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:border-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-line text-xl"></i>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:border-white transition-colors cursor-pointer">
                  <i className="ri-twitter-x-line text-xl"></i>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:border-white transition-colors cursor-pointer">
                  <i className="ri-facebook-line text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="w-10 h-10 border border-gray-500 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:border-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}